/**
 * useGooglePlaces — minimal hook that:
 *   1. Lazily loads the Google Maps JS Places library (once, app-wide).
 *   2. Returns predictions as the user types via AutocompleteService.
 *   3. Resolves a selected prediction to a structured address via PlacesService.
 *
 * Why not the new <PlaceAutocompleteElement> web component? It works, but
 * styling it to match the rest of the form requires a Shadow DOM workaround.
 * The legacy AutocompleteService API gives us a plain <input> we control.
 *
 * Required env: REACT_APP_GOOGLE_MAPS_API_KEY
 *
 * Restrict the key in Google Cloud Console (HTTP referrer + Places API only).
 */
import { useEffect, useRef, useState, useCallback } from 'react';

let scriptPromise = null;
function loadGoogleMaps(apiKey) {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (!apiKey) {
      reject(new Error('Google Maps API key not configured (REACT_APP_GOOGLE_MAPS_API_KEY)'));
      return;
    }
    const cbName = `__nbGmapsCb_${Date.now()}`;
    window[cbName] = () => {
      delete window[cbName];
      resolve(window.google);
    };
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&callback=${cbName}&loading=async`;
    s.async = true;
    s.defer = true;
    s.onerror = () => {
      delete window[cbName];
      scriptPromise = null;
      reject(new Error('Failed to load Google Maps script'));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Returns:
 *   - ready    : boolean, true once the JS API is loaded
 *   - error    : string or null
 *   - search(q): kicks off a prediction lookup; updates `predictions`
 *   - predictions: [{description, place_id}]
 *   - resolve(place_id): returns a normalised address breakdown {formatted, line1, city, state, postal, country}
 */
export default function useGooglePlaces() {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const autoRef = useRef(null);
  const placeRef = useRef(null);
  const tokenRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    // Google calls window.gm_authFailure() when the key is invalid, the Places
    // API isn't enabled, or the referrer isn't allow-listed. The script still
    // loads, but every Places call silently returns REQUEST_DENIED — so this
    // is the only reliable place to surface a runtime auth problem to the UI.
    const prevAuthFail = window.gm_authFailure;
    window.gm_authFailure = () => {
      if (!cancelled) setError('Address autocomplete unavailable (API key restricted or Places API not enabled).');
      if (typeof prevAuthFail === 'function') try { prevAuthFail(); } catch { /* noop */ }
    };
    loadGoogleMaps(apiKey)
      .then((google) => {
        if (cancelled) return;
        autoRef.current = new google.maps.places.AutocompleteService();
        // PlacesService needs a DOM node to attach to; an off-screen div is fine.
        const dummy = document.createElement('div');
        placeRef.current = new google.maps.places.PlacesService(dummy);
        tokenRef.current = new google.maps.places.AutocompleteSessionToken();
        setReady(true);
      })
      .catch((e) => { if (!cancelled) setError(e.message || 'Could not load Google Maps'); });
    return () => { cancelled = true; };
  }, [apiKey]);

  const search = useCallback((query) => {
    if (!ready || !autoRef.current) return;
    const q = (query || '').trim();
    if (q.length < 3) { setPredictions([]); return; }
    autoRef.current.getPlacePredictions(
      { input: q, sessionToken: tokenRef.current, types: ['address'] },
      (results, status) => {
        // ZERO_RESULTS is fine (just no matches). REQUEST_DENIED / INVALID_REQUEST
        // signal a config problem (key restricted, Places API not enabled, billing off).
        // Surface those so AddressAutocompleteInput can fall back to its plain-input warning.
        if (status === 'REQUEST_DENIED' || status === 'INVALID_REQUEST') {
          setError('Address autocomplete unavailable (Places API request denied).');
          setPredictions([]);
          return;
        }
        if (status !== 'OK' || !results) {
          setPredictions([]);
          return;
        }
        setPredictions(results.map((r) => ({ description: r.description, place_id: r.place_id })));
      }
    );
  }, [ready]);

  const resolve = useCallback((placeId) => {
    return new Promise((resolveCb, rejectCb) => {
      if (!ready || !placeRef.current) {
        rejectCb(new Error('Places service not ready'));
        return;
      }
      placeRef.current.getDetails(
        { placeId, fields: ['address_components', 'formatted_address', 'geometry'], sessionToken: tokenRef.current },
        (place, status) => {
          // Rotate the session token after a successful resolve (per Google's billing recs).
          if (window.google?.maps?.places) {
            tokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
          }
          if (status !== 'OK' || !place) {
            rejectCb(new Error('Could not resolve address'));
            return;
          }
          const get = (type, useShort = false) => {
            const c = (place.address_components || []).find((cc) => cc.types.includes(type));
            return c ? (useShort ? c.short_name : c.long_name) : '';
          };
          const streetNumber = get('street_number');
          const route = get('route');
          const line1 = [streetNumber, route].filter(Boolean).join(' ').trim();
          resolveCb({
            formatted: place.formatted_address || '',
            line1,
            city: get('locality') || get('postal_town') || get('sublocality') || '',
            state: get('administrative_area_level_1', true),
            postal: get('postal_code'),
            country: get('country', true),
            country_long: get('country'),
            lat: place.geometry?.location?.lat?.() ?? null,
            lng: place.geometry?.location?.lng?.() ?? null,
          });
        }
      );
    });
  }, [ready]);

  const clear = useCallback(() => setPredictions([]), []);

  return { ready, error, predictions, search, resolve, clear };
}
